/** Timeout, polling, retry, and debouncing constants. */

import { TIME_UNITS } from '@federa/const/time-units';
import type { RetryConfig } from '@federa/types/api-response';

/** UI toast / copy-feedback dismiss timeouts (ms), one tuner for the whole feedback envelope: `short` =
 * clipboard-copy pulse, `standard` = generic status banner, `long` = notice/error banners needing read time. */
export const TOAST_DISMISS_MS = {
	short: 2000,
	standard: 3000,
	long: 5000,
} as const;

/** Long-running spawn subprocesses and structured-agent post-LLM conversion budgets. */
export const FIVE_MINUTES_MS = 5 * TIME_UNITS.MINUTE_MS;

/**
 * How long the explore feed waits after its store last changed before rewriting the localStorage properties cache
 * (`components/lib/properties-cache.ts`). The store moves on every hub delta -- a bid, an extension, the subscribe-time
 * snapshot -- and each rewrite serialises the whole feed, so the write trails the burst rather than riding it. One
 * second is well inside the gap between a viewer leaving and coming back, which is the only moment the cache is read.
 */
export const PROPERTIES_CACHE_WRITE_DEBOUNCE_MS = TIME_UNITS.SECOND_MS;

/**
 * Wall budget of ONE `npm audit` invocation inside the `npm-audit` step of `flows/npm-audit-scan-host.ts`. The step
 * runs two invocations back to back (prod tier, full tier), and this budget is the OPERAND the step's own wall is
 * derived from rather than a figure fitted under it: `NPM_AUDIT_SCAN.AUDIT_STEP_WALL` (`const/npm-audit-scan-io.ts`)
 * is this value times the tier count plus parse headroom, so the step outlives both children by construction and this
 * const can move without leaving a parked number above it. The ordering is what matters: at a budget that let the STEP
 * fire first, a hung endpoint failed the whole run where the runner's own timeout would have handed the governance
 * mapper its UNAVAILABLE rows (witnessed 2026-09-04 under the retired compliance-scan composer, whose step wall was a
 * flat five minutes: registry ping 200 in 0.2 s, both audit POST endpoints 0 bytes at 15 s, `npm audit` silent past
 * 120 s). A healthy audit measures in ~11 s, so two minutes is the outage arm only.
 */
export const NPM_AUDIT_INVOCATION_TIMEOUT_MS = 2 * TIME_UNITS.MINUTE_MS;

/**
 * How long a post-login navigation waits for an IN-FLIGHT session mint before going ahead without it
 * (`awaitAppSession`, `apps/federa/src/lib/auth/app-session-sync.ts`). The pending action of a gated page used to
 * race the mint: the destination was server-rendered before the signed cookie existed, so its preload island resolved
 * no viewer and the surface paid a second client fetch after landing (2026-09-03). Bounded so a mint that hangs cannot
 * hold the navigation.
 */
export const APP_SESSION_NAVIGATE_WAIT_MS = 3 * TIME_UNITS.SECOND_MS;

/**
 * The ceiling an idle-scheduled WARMER waits for browser idle time before running anyway (`scheduleIdle`,
 * `utils/browser/schedule-idle.ts`): the mobile assistant pre-warm and the next-hop bundle preloads. Long enough to
 * stay behind the page's own first paint, short enough that a viewer heading for the next surface still finds it warm.
 * Also the whole wait on engines without `requestIdleCallback` (Safari).
 */
export const IDLE_WARM_FALLBACK_TIMEOUT_MS = 4 * TIME_UNITS.SECOND_MS;

export const HEARTBEAT_INTERVAL_MS = 30000;
/** Service-binding flow call timeout. AI/agent flows legitimately run for many minutes, so this MATCHES the per-step
 * budget on the AI execution-chain flow leaves; a shorter ceiling trips those calls before the inner steps get a
 * chance. Wall-clock, not CPU. */
export const FLOW_BINDING_TIMEOUT_MS = 30 * TIME_UNITS.MINUTE_MS;
export const CIRCUIT_BREAKER_COOLDOWN_MS = 30000;
/** Consecutive failures before a circuit breaker opens. */
export const CIRCUIT_BREAKER_THRESHOLD = 5;

/** Per-callsite windows (ms) for `createDeduplicationCache`; each key is a distinct dedup site with its own time-window semantics. */
export const DEDUP_WINDOWS = {
	/** Activity tracking double-mount guard (root layout renders desktop+mobile). */
	activityTrack: 2000,
	/** Error fingerprint dedup. */
	errorReporting: 300_000,
	/** WebSocket property dispatch identity skip. */
	propertyDetail: 5000,
	/** Optimistic chat message reconciliation window. Mirrored by DEDUP_THRESHOLD_MS. */
	wsMessage: 10_000,
} as const;

export const POLLING_INTERVALS = {
	connectionFast: 500,
	connectionStandard: 2000,
	dataRefreshFast: 5000,
	dataRefreshMedium: 15000,
	dataRefreshStandard: 30000,
	dataRefreshSlow: 60000,
	routeChangeCheck: 500,
	countdownSync: 5000,
} as const;

// Every member here must have an ENFORCING consumer: an unenforced ceiling implies a contract nothing honors.
export const API_TIMEOUTS = {
	standard: 10000,
	longRunning: 30000,
	placesApi: 10000,
} as const;

/**
 * `auth-login`'s PROVISIONING ceiling, in seconds -- the bound on its `user-provision` subflow, which both the
 * existing-user and the create-user arms declare. Sized off that child's declared critical path: a 330s serial chain
 * (`flows/auth-login.ts` carries the leg-by-leg derivation) plus 25% headroom.
 *
 * SPELLED HERE BECAUSE THREE SURFACES MUST AGREE and two of them are not the flow: the flow's two subflow steps, and
 * the BROWSER's own deadline on `POST /login` below. A client bound tighter than this one does not end a hang -- it
 * polices a slow answer, and the answer it cuts short is a login whose provisioning is still WRITING.
 */
export const AUTH_LOGIN_PROVISION_TIMEOUT_SEC = 415;

/**
 * The browser's deadline for `POST /login` (`AuthClient.login`). DERIVED from the worker's own ceiling, never chosen
 * beside it: the client may only give up once the worker itself would have, so tripping this means the worker died
 * silently rather than that provisioning ran long. One `standard` round trip of margin covers the response's own
 * travel after the flow settles.
 *
 * WHY IT IS NOT `API_TIMEOUTS.longRunning`. At 30s against a 415s declared step, a first-time signup on a slow vendor
 * leg aborted client-side while the worker kept provisioning; the refused answer reaches the session machine as a
 * failed login, which signs Firebase out -- the person is signed out with the account half-created and the SMS spent,
 * and must request a new code. The bound exists to END a hang, never to police a slow answer.
 */
export const AUTH_LOGIN_CLIENT_TIMEOUT_MS = AUTH_LOGIN_PROVISION_TIMEOUT_SEC * TIME_UNITS.SECOND_MS + API_TIMEOUTS.standard;

/**
 * Wall an MFE mount gives ONE injected `<script type="module">` to publish its `window.__federaMFE_<page>` global before
 * the mount declares the bundle unreachable (`components/common/useMFEContainerMount.ts`).
 *
 * It exists because `script.onerror` alone does not close the failure set: a 404 or a network drop fires it, but a
 * bundle that PARSES and then throws before its `createMFEMount` call resolves the script cleanly and publishes
 * nothing -- an empty container with no error anywhere. This is the only observer of that second case.
 *
 * Well above `API_TIMEOUTS.standard`: this budget covers a cold asset fetch plus parse plus the bundle's own module
 * evaluation on a slow device, not one round trip, and a premature fire would replace a working panel with a retry
 * button. A late load is still accepted -- `nextMfeMountStatus` takes `loaded` from `failed`.
 */
export const MFE_BUNDLE_LOAD_TIMEOUT_MS = 20_000;

/** Default bounded retry budget for sites with no domain-specific RetryConfig; any such config supersedes it.
 *  (The DEFAULT_RETRY_ATTEMPTS synonym was folded into this single scalar; declared HERE rather than in
 *  observability-retry because that leaf imports this one -- the reverse edge would cycle.) */
export const DEFAULT_MAX_RETRIES = 3;

/** Default exponential-backoff multiplier when `RetryConfig.backoffMultiplier` is omitted -- a doubling backoff. Per-domain RetryConfig instances may override. */
export const DEFAULT_BACKOFF_MULTIPLIER = 2;

export const GENERIC_RETRY_CONFIG = {
	maxAttempts: DEFAULT_MAX_RETRIES,
	baseDelayMs: 1000,
	maxDelayMs: 30000,
	backoffMultiplier: DEFAULT_BACKOFF_MULTIPLIER,
} as const satisfies RetryConfig;

export const WEBSOCKET_RETRY_CONFIG = {
	maxAttempts: 5,
	baseDelayMs: 1000,
	maxDelayMs: 30000,
} as const satisfies RetryConfig;

/** Cap on the initial-data socket re-requests before `useDataRequestRetry` gives up (the socket may be down). */
export const DATA_REQUEST_MAX_RETRIES = 5;

/** Poll cadence of the spawned-agent wait loop: short enough that a freed slot is observed promptly, long enough not to spin. */
export const AGENT_RESULT_POLL_INTERVAL_MS = 500;

/** Retries the global spawn-slot gate makes when a slot RELEASE loses a read-modify-write race against a peer. */
export const SPAWN_SLOT_RELEASE_CONTENTION_RETRY_LIMIT = 3;

/** Poll cadence of `waitForAgentProcess`'s `agent.finished` loop (`tools/agents/source-aggregate-helpers.ts`). */
export const AGENT_PROCESS_WAIT_POLL_INTERVAL_MS = 200;

/** Poll cadence the global spawn-slot gate falls back to when no caller-supplied wait cadence is available. */
export const SPAWN_SLOT_GATE_POLL_FALLBACK_MS = 200;

/**
 * Re-read budgets for an agent's log, grouped because both readers race the SAME flush: the CLI writes the terminal
 * NDJSON line asynchronously, so the first read after process exit can legitimately miss it and these bound the
 * re-read rather than conceding an empty parse. `PARSE_*` is the structured-output parse budget; `READ_RETRIES` is
 * the looser raw-reader budget, which waits on the file existing at all.
 */
export const AGENT_LOG_RETRY = {
	PARSE_READ_RETRIES: 10,
	PARSE_READ_RETRY_DELAY_MS: 200,
	READ_RETRIES: 30,
} as const;

/** Per-navigation ceiling for the realauction Playwright driver's `page.goto` calls. */
export const REALAUCTION_PAGE_GOTO_TIMEOUT_MS = 30_000;

/** Wall for the prompt-cache seed spawn; a seed is one short conversation, so it never needs the full agent budget. */
export const AGENT_SEED_SPAWN_TIMEOUT_MS = 180_000;

/** Wall for the `wrangler d1 export --remote` child behind the live-schema dump refresh
 * (`tools/migrations/live-schema-export.ts`). INSIDE the minute its `export-dump` step declares (iss-a777a8d6), so the
 * leash fires before the step's budget abandons the child: every recorded `live-schema-export-host` run in the flow-run
 * journals (seven, 2026-09-22..28) finished its WHOLE graph in 2.9-4.0 s, so 45 s is a wedge detector, not a schedule. */
export const LIVE_SCHEMA_EXPORT_TIMEOUT_MS = 45_000;

/**
 * Child-side wall for one `wrangler d1 execute --remote` spawn on the migration APPLY and repair-patch paths
 * (`executeMigration` / `executePatchSql`, `tools/migrations/migration-execution.ts`).
 *
 * WHY A BOUND HAS TO EXIST AT ALL (ledger A78-051). Both spawns passed NO `timeout`, so the child had no leash of its
 * own and the only bound in play was the enclosing `execute` node's then-840s budget -- which the engine enforces as a
 * `Promise.race`, i.e. by ABANDONING the continuation rather than by killing anything. A wedged spawn therefore left a
 * live wrangler chain still writing to a remote database with nothing holding it, on the one path in this repo that
 * MUTATES money data.
 *
 * WHY IT IS ENFORCEABLE ONLY NOW. `execFile`'s own timeout kill signals the process it spawned. Under the retired
 * `npx` head that process was `cmd.exe`, with `npx.cmd` and the real `node wrangler.js` as grandchildren that Windows
 * leaves running -- witnessed on migration 0186, whose DDL landed AFTER its host child was SIGKILLed, twice. With the
 * shell-free head (`wranglerSpawnTarget`, `utils/wrangler/spawn-target.ts`) the spawned process IS the node/wrangler
 * process, so the kill reaches it directly and no `taskkill /T` tree walk is owed.
 *
 * SIZED UNDER THE NODE BUDGET: strictly below the `execute` step's minute (iss-a777a8d6: one file per confirmed run,
 * `FLOW_RUN_MINUTE_WALL.STEP_WALL`) so the leash can actually fire BEFORE the engine abandons the continuation -- a leash
 * above that budget would be decorative in exactly the case it exists for. High enough that no honest remote statement is
 * truncated: a confirmed one-file apply measured 22.5 s for its WHOLE host segment on 2026-09-29/30
 * (run-1790676397661-b535442c57d2, run-1790697294788-a756b060ebb0), and a single tracker read is ~3.7 s. It is a WEDGE
 * detector, not a per-run schedule; the synchronous runner (`tools/migrations/applied-migrations-runner.ts`) takes the
 * same leash, because a blocked thread cannot fire the step's budget at all.
 */
export const MIGRATION_D1_EXECUTE_TIMEOUT_MS = 45_000;

/**
 * Child-side wall for the ONE read-only `wrangler d1 execute --remote --command <capped SELECT> --json` spawn behind
 * the unbound row probe (`tools/migrations/d1-unbound-select-probe.ts`).
 *
 * SIZED ON ITS OWN AXIS, not against its two neighbours: {@link MIGRATION_D1_EXECUTE_TIMEOUT_MS} and
 * {@link LIVE_SCHEMA_EXPORT_TIMEOUT_MS} were once the mutation-sized leashes this one sat far below, and both were cut
 * to 45 s inside the minute wall (iss-a777a8d6), so the comparison no longer argues anything. This spawn is a single
 * SELECT executing under a structural `LIMIT` wrap
 * (`SQL_PROBE_ROW_CAP`, `const/sql-probe-row-cap.ts`), so its honest duration is a remote round trip plus a
 * capped read and nothing in the input can grow it. Inheriting a mutation-sized leash would make it decorative
 * exactly where it is cheapest to be real: a probe that hangs should surface as a timeout inside the operator's
 * dispatch, not sit on the host for ten minutes.
 */
export const UNBOUND_D1_SELECT_PROBE_TIMEOUT_MS = 60_000;

/**
 * Post-download settle budget for the title document pipeline. The JS download trigger returns BEFORE Chrome finishes
 * writing the file, so a ref absent from disk on the first pass is usually still in flight rather than genuinely missing;
 * the pipeline re-stages this many times, sleeping this long between passes, before declaring a download absent.
 */
export const CHROME_DOWNLOAD_SETTLE_RETRY_ATTEMPTS = 3;
export const CHROME_DOWNLOAD_SETTLE_DELAY_MS = 3000;

/**
 * Ceiling on the OTLP collector POST made by the detached PostToolUseFailure telemetry child.
 *
 * Governs one loopback HTTP round trip from a fire-and-forget hook child. Deliberately NOT shared with
 * `HOOK_GIT_PROBE_TIMEOUT_MS` despite the equal duration: that one bounds how long a SYNCHRONOUS git probe may stall a
 * hook, and the two are retuned for opposite reasons -- the git bound tracks repository size, this one tracks collector
 * responsiveness. Coupling them would make a retune of either silently move the other.
 */
export const TOOL_FAILURE_TELEMETRY_EMIT_TIMEOUT_MS = 3 * TIME_UNITS.SECOND_MS;

/** Absolute ceiling preserving the property that a wedged child can never hang `git commit` indefinitely. */
export const PRE_COMMIT_GATE_DEADLINE_CEILING_MS = 8 * TIME_UNITS.MINUTE_MS;

// MFE

/** How long a host remembers an MFE entry's version before asking the MFE worker again (iss-9d956529). SHORT ON
 *  PURPOSE: a page rendered inside this window after a deploy still names the previous version, and while a fresh
 *  browser is then served the new entry `no-store` (`forwardServiceBindingPaths`), a browser that already holds the
 *  previous version's URL runs it from cache -- and the previous build's chunks are gone after a redeploy. Five
 *  seconds bounds that exposure to a deploy's first few seconds, for one binding round trip per MFE per window. */
export const MFE_ENTRY_VERSION_TTL_MS = 5 * TIME_UNITS.SECOND_MS;

/** The most a page render waits on ONE MFE worker for its entry version (iss-9d956529): a slower or hung binding is a
 *  slug with no version that window, and its mounts dial the bare stable entry, so the page never stalls on it. */
export const MFE_ENTRY_VERSION_READ_TIMEOUT_MS = TIME_UNITS.SECOND_MS;

/** How often a long-open tab asks whether its MFE has a newer build (`useMfeEntryUpdate`): one HEAD of the bare entry
 *  per mounted MFE per window. Its own name, not `FIVE_MINUTES_MS`, so retuning how soon a tab hears of a deploy moves
 *  nothing else that happens to last five minutes. */
export const MFE_UPDATE_CHECK_INTERVAL_MS = 5 * TIME_UNITS.MINUTE_MS;
